import dayjs, { type Dayjs } from 'dayjs';
import * as z from 'zod';

import {
  BONUS_ELIGIBILITY_OPTIONS,
  BONUS_STATUS_OPTIONS_CUSTOM,
  BONUS_STATUS_OPTIONS_ONGOING,
  BONUS_TIMEFRAME_OPTIONS,
  CASHBACK_BONUS_PRODUCT_TYPE_OPTIONS,
} from '../constants';
import { CAMPAIGN_REWARD_TYPE } from '../constants/enums';
import { generateZodEnumIds } from '../utils';
import {
  itemInformationValidator,
  itemNameValidator,
  nullableCoercedNumberInput,
  nullableNumberInput,
} from './helpers';

const MAX_INT32 = 2147483647;

const requiredPositiveAmount = (message: string) =>
  nullableCoercedNumberInput().refine((value): value is number => value !== null && value > 0, { message });

const freeSpinCountSchema = nullableCoercedNumberInput().refine(
  (value): value is number => value !== null && value > 0 && Number.isInteger(value),
  { message: 'Free spins awarded must be a positive whole number.' }
);

const betValueSchema = nullableCoercedNumberInput().refine(
  (value): value is number => value !== null && value > 0 && Math.abs(value * 100 - Math.round(value * 100)) <= 1e-8,
  { message: 'Bet value per spin must be positive with no more than 2 decimal places.' }
);

const cashbackFreeSpinTierFormValuesSchema = z.object({
  aggregator: z.enum(['GGA', 'GOLDGAMES']).optional(),
  goldGames: z.object({ betLevel: z.number(), coinValue: z.number(), validityHours: z.number() }).optional(),
  minLossAmount: nullableCoercedNumberInput(),
  maxLossAmount: nullableCoercedNumberInput(),
  freeSpinsAwarded: nullableCoercedNumberInput(),
  betValuePerSpin: nullableCoercedNumberInput(),
  providerId: z.string(),
  providerName: z.string(),
  gameId: z.string(),
  gameName: z.string(),
});

export type CashbackFreeSpinTierFormValues = z.infer<typeof cashbackFreeSpinTierFormValuesSchema>;

export const cashbackFreeSpinTierSchema = z
  .object({
    aggregator: z.enum(['GGA', 'GOLDGAMES']).optional(),
    goldGames: z
      .object({
        betLevel: z.number(),
        coinValue: z.number(),
        validityHours: z.number().int().min(1).max(720),
      })
      .optional(),
    minLossAmount: requiredPositiveAmount('Min loss amount must be greater than 0.'),
    maxLossAmount: nullableCoercedNumberInput().refine((value) => value === null || value > 0, {
      message: 'Max loss amount must be greater than 0.',
    }),
    freeSpinsAwarded: nullableCoercedNumberInput(),
    betValuePerSpin: nullableCoercedNumberInput(),
    providerId: z.string().min(1, 'Provider is required.'),
    providerName: z.string().min(1, 'Provider is required.'),
    gameId: z.string().min(1, 'Game is required.'),
    gameName: z.string().min(1, 'Game is required.'),
  })
  .superRefine((tier, ctx) => {
    const countValid = freeSpinCountSchema.safeParse(tier.freeSpinsAwarded).success;
    const amountValid = betValueSchema.safeParse(tier.betValuePerSpin).success;
    if (tier.aggregator === 'GOLDGAMES') {
      const settings = tier.goldGames;
      const settingsValid =
        settings &&
        Number.isInteger(settings.betLevel) &&
        settings.betLevel >= 0 &&
        settings.betLevel <= MAX_INT32 &&
        Number.isFinite(settings.coinValue) &&
        settings.coinValue > 0;
      if (!countValid || !amountValid || !settingsValid || Number(tier.freeSpinsAwarded) > MAX_INT32)
        ctx.addIssue({ code: 'custom', message: 'Select an available Gold Games preset.', path: ['betValuePerSpin'] });
    } else {
      if (!countValid)
        ctx.addIssue({
          code: 'custom',
          message: 'Free spins awarded must be a positive whole number.',
          path: ['freeSpinsAwarded'],
        });
      if (!amountValid)
        ctx.addIssue({
          code: 'custom',
          message: 'Bet value per spin must be positive with no more than 2 decimal places.',
          path: ['betValuePerSpin'],
        });
    }
  })
  .refine((tier) => tier.maxLossAmount === null || tier.maxLossAmount > tier.minLossAmount, {
    message: 'Max loss amount must be greater than min loss amount.',
    path: ['maxLossAmount'],
  })
  .transform((tier) => ({
    ...tier,
    freeSpinsAwarded: Number(tier.freeSpinsAwarded),
    betValuePerSpin: Number(tier.betValuePerSpin),
  }));

export type CashbackFreeSpinTierData = z.infer<typeof cashbackFreeSpinTierSchema>;

const validateGoldGamesTierGames = (tiers: CashbackFreeSpinTierData[], ctx: z.RefinementCtx) => {
  const seenGames = new Set<string>();
  tiers.forEach((tier, index) => {
    if (tier.aggregator === 'GOLDGAMES' && seenGames.has(tier.gameId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Gold Games tiers must use different games to avoid overlapping campaigns.',
        path: [index, 'gameId'],
      });
    }
    seenGames.add(tier.gameId);
  });
};

const validateTierLossRanges = (tiers: CashbackFreeSpinTierData[], ctx: z.RefinementCtx) => {
  const sortedTiers = tiers
    .map((tier, index) => ({ tier, index }))
    .sort((left, right) => left.tier.minLossAmount - right.tier.minLossAmount);
  let coveredThrough = Number.NEGATIVE_INFINITY;

  sortedTiers.forEach(({ tier, index }) => {
    if (tier.minLossAmount <= coveredThrough) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Loss ranges must not overlap.',
        path: [index, 'minLossAmount'],
      });
    }
    coveredThrough = Math.max(coveredThrough, tier.maxLossAmount ?? Number.POSITIVE_INFINITY);
  });
};

const cashbackFreeSpinTiersSchema = z
  .array(cashbackFreeSpinTierSchema)
  .min(1, 'At least one free spins tier is required.')
  .superRefine((tiers, ctx) => {
    validateTierLossRanges(tiers, ctx);
    validateGoldGamesTierGames(tiers, ctx);
  });

const cashbackBonusCommonSchema = z.object({
  name: itemNameValidator().pipe(z.string()),
  eligibility: z.enum(generateZodEnumIds(BONUS_ELIGIBILITY_OPTIONS)),
  validityPeriod: z.enum(['ONGOING', 'CUSTOM']),
  fromTimestamp: z.custom<Dayjs>((value) => value instanceof dayjs, 'Invalid date').nullish(),
  toTimestamp: z.custom<Dayjs>((value) => value instanceof dayjs, 'Invalid date').nullish(),
  status: z.enum([
    ...generateZodEnumIds(BONUS_STATUS_OPTIONS_CUSTOM),
    ...generateZodEnumIds(BONUS_STATUS_OPTIONS_ONGOING),
  ]),
  schedule: z.enum(generateZodEnumIds(BONUS_TIMEFRAME_OPTIONS)),
  userIds: z.array(z.string()),
  description: itemInformationValidator,
});

export type CashbackBonusFormValues = z.input<typeof cashbackBonusFormSchema>;

const realMoneyCashbackSchema = cashbackBonusCommonSchema.extend({
  product: z.enum(generateZodEnumIds(CASHBACK_BONUS_PRODUCT_TYPE_OPTIONS)),
  rewardType: z.literal(CAMPAIGN_REWARD_TYPE.REAL_MONEY),
  refundPercentage: nullableNumberInput().refine(
    (value): value is number => value !== null && value >= 1 && value <= 100,
    { message: 'Must be between 1 and 100' }
  ),
  minimumLostAmount: nullableNumberInput().refine((value): value is number => value !== null && value >= 0, {
    message: 'Minimum lost amount is required.',
  }),
  maximumRefundAmount: nullableNumberInput().refine((value): value is number => value !== null && value >= 0, {
    message: 'Maximum refund amount is required.',
  }),
  // Tier drafts are irrelevant to Real Money and must never block its submission.
  freeSpinTiers: z.array(cashbackFreeSpinTierFormValuesSchema).catch([]),
});

const freeSpinsCashbackSchema = cashbackBonusCommonSchema.extend({
  product: z.literal('CASINO', {
    error: 'Free spins are only available for Casino cashback.',
  }),
  rewardType: z.literal(CAMPAIGN_REWARD_TYPE.FREE_SPINS),
  refundPercentage: nullableNumberInput(),
  minimumLostAmount: nullableNumberInput(),
  maximumRefundAmount: nullableNumberInput(),
  freeSpinTiers: cashbackFreeSpinTiersSchema,
});

export const cashbackBonusFormSchema = z
  .discriminatedUnion('rewardType', [realMoneyCashbackSchema, freeSpinsCashbackSchema])
  .superRefine((data, ctx) => {
    if (data.validityPeriod !== 'CUSTOM') return;

    if (!data.fromTimestamp) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'From is required.', path: ['fromTimestamp'] });
    }
    if (!data.toTimestamp) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'To is required.', path: ['toTimestamp'] });
    }
    if (data.fromTimestamp && data.toTimestamp && !data.toTimestamp.isAfter(data.fromTimestamp)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'To must be after From.', path: ['toTimestamp'] });
    }
  });

export type CashbackBonusFormData = z.infer<typeof cashbackBonusFormSchema>;
